import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ADVANTAGE_ITEM_DEFS,
  AXIS_META,
  AXIS_ORDER,
  FactorDef,
  RATING_SCALE,
  SalesDataLinkageInputState,
} from "./types";
import { RatingIcon } from "./Common";
import { getFactorDisplay, getIncludedAxes } from "./utils";
import { config } from "../../../assets/config";
import { getIdToken } from "../../../utils/authTokenStorage";
import { ensureValidIdToken, refreshTokenWithLock } from "../../../utils/awsCognito";
import { WebAPIContext } from "../../../contexts/WebAPIContext";
import { ProjectContext } from "../InvestmentScreeningDetail";

interface Props {
  state: SalesDataLinkageInputState;
  isEditable: boolean;
  namespace: string;
  /** 保存/復元先を特定するためのプロジェクトID */
  projectId?: string;
  /** markAsSaved=trueの場合、呼び出し元（SalesDataLinkage）が未保存判定の基準値を更新後stateで更新する */
  onChange: (patch: Partial<SalesDataLinkageInputState>, markAsSaved?: boolean) => void;
  /** AI試算結果（要因別ストレス）をストレス指数（ダウンサイド指数）へ反映する際のコールバック */
  onReflect?: (factors: FactorDef[]) => void;
}

interface InputRow {
  label: string;
  value: React.ReactNode;
  color: string;
}

interface InputGroup {
  title: string;
  rows: InputRow[];
}

/** AI算出ボタン・空状態のスパークルアイコン（design_handoff準拠） */
const SparkleIcon: React.FC<{ size?: number; stroke?: string; strokeWidth?: number }> = ({
  size = 17,
  stroke = "currentColor",
  strokeWidth = 2,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4L12 3z" />
    <path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const AiStep: React.FC<Props> = ({ state, isEditable, namespace, projectId, onChange, onReflect }) => {
  const { t } = useTranslation();
  const tr = (key: string, opts: Record<string, unknown> = {}): string =>
    t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`, opts) as unknown as string;
  const trFactor = (key: string): string =>
    t(`features.InvestmentScreening.${namespace}.downsideIndex.column.${key}`) as unknown as string;
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const includedAxes = getIncludedAxes(state.axisInclude);
  const achieveAxes = includedAxes.length > 0 ? includedAxes : AXIS_ORDER;

  // ---- 分析インプット（design_handoff準拠：カテゴリごとにグループ化） ----
  const groups: InputGroup[] = [];
  groups.push({
    title: tr("aiGroup.scope"),
    rows: [
      {
        label: tr("label.targetAxis"),
        value:
          includedAxes.length > 0
            ? tr("label.axisCountFormat", {
                n: includedAxes.length,
                labels: includedAxes.map((k) => tr(`tab.${AXIS_META[k].labelKey}`)).join("・"),
              })
            : tr("label.noneIncluded"),
        color: "#334155",
      },
    ],
  });
  groups.push({
    title: tr("aiGroup.axisData"),
    rows: achieveAxes.flatMap((k) => {
      const meta = AXIS_META[k];
      const r = RATING_SCALE.find((x) => x.val === state.axisEvals[k]);
      return [
        {
          label: `${tr(`tab.${meta.labelKey}`)}　${tr("label.achieveRate")}`,
          value: `${meta.achieve.toFixed(1)}%`,
          color: meta.achieve >= 100 ? "#16a34a" : "#dc2626",
        },
        {
          label: `${tr(`tab.${meta.labelKey}`)}　${tr("label.axisEvaluation")}`,
          value: r ? (
            <HStack spacing="4px" justify="flex-end">
              <RatingIcon val={r.val} size={16} />
              <Text as="span">{tr(`ratingScale.${r.labelKey}`)}</Text>
            </HStack>
          ) : (
            tr("label.notEvaluated")
          ),
          color: r ? r.color : "#94a3b8",
        },
      ];
    }),
  });
  ADVANTAGE_ITEM_DEFS.forEach((item) => {
    const rows: InputRow[] = item.radios.map((rg) => {
      const selected = rg.options.find((o) => o.v === state.advRadios[`${item.key}.${rg.key}`]);
      return {
        label: tr(`advantage.${rg.labelKey}`),
        value: selected ? tr(`advantage.${selected.labelKey}`) : tr("label.notEntered"),
        color: selected ? "#334155" : "#94a3b8",
      };
    });
    const r = RATING_SCALE.find((x) => x.val === state.evals[item.key]);
    rows.push({
      label: tr("label.fiveScaleEvaluation"),
      value: r ? (
        <HStack spacing="4px" justify="flex-end">
          <RatingIcon val={r.val} size={16} />
          <Text as="span">{tr(`ratingScale.${r.labelKey}`)}</Text>
        </HStack>
      ) : (
        tr("label.notEvaluated")
      ),
      color: r ? r.color : "#94a3b8",
    });
    groups.push({
      title: tr("aiGroup.advantage", { label: tr(`advantage.${item.labelKey}`) }),
      rows,
    });
  });
  const rOverall = RATING_SCALE.find((x) => x.val === state.overallRating);
  groups.push({
    title: tr("aiGroup.overall"),
    rows: [
      {
        label: tr("label.overallRating"),
        value: rOverall ? (
          <HStack spacing="4px" justify="flex-end">
            <RatingIcon val={rOverall.val} size={16} />
            <Text as="span">{tr(`ratingScale.${rOverall.labelKey}`)}</Text>
          </HStack>
        ) : (
          tr("label.notEntered")
        ),
        color: rOverall ? rOverall.color : "#94a3b8",
      },
      { label: tr("label.confidence"), value: `${state.confidence} %`, color: "#2f6bd4" },
    ],
  });

  // ---- 分析結果：要因別ストレス（design_handoff準拠：符号付き%＋バー＋5段階配色） ----
  // 値はAI試算結果（state.aiFactors）を使用する
  const factors = (state.aiFactors ?? []).map((f) => ({
    label: trFactor(f.labelKey),
    ...getFactorDisplay(f.v),
  }));

  /** バックエンドAI分析APIへ送るインプット（対象軸のみ／未入力項目は除外） */
  const buildFactorAnalysisInput = () => ({
    // 対象/除外の選択状態（軸ごと）
    axisInclude: state.axisInclude,
    // 検索ボタンの押下状態（軸ごと。検索実行済みかどうか）
    searched: state.searched,
    // 検索条件（フィルタ）の選択状態（開始年月／終了年月も含む）
    filterValues: { ...state.filterValues, STARTYM: state.startYm, ENDYM: state.endYm },
    // 検索結果表示タブ（金額／単価／数量）の選択状態
    resultMetric: state.resultMetric,
    axes: includedAxes.map((k) => ({
      key: k,
      achieveRate: AXIS_META[k].achieve,
      rating: state.axisEvals[k] ?? null,
      note: state.axisNotes[k] || undefined,
    })),
    advantage: ADVANTAGE_ITEM_DEFS.map((item) => ({
      key: item.key,
      rating: state.evals[item.key] ?? null,
      note: state.advNotes[item.key] || undefined,
      radios: item.radios.reduce<Record<string, string>>((acc, rg) => {
        const v = state.advRadios[`${item.key}.${rg.key}`];
        if (v) acc[rg.key] = v;
        return acc;
      }, {}),
    })),
    overall: {
      rating: state.overallRating,
      note: state.overallNote || undefined,
      confidence: state.confidence,
    },
  });

  const callAiLambda = async (idToken: string) =>
    fetch(config.aiLambdaUrl, {
      method: "POST",
      headers: {
        Authorization: idToken,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mode: "salesDataLinkageFactorAnalysis",
        input: buildFactorAnalysisInput(),
      }),
    });

  const handleRun = async () => {
    onChange({ analysisStatus: "running", aiErrorMessage: null });
    try {
      // 呼び出し前にidTokenの有効期限を確認し、期限切れ/間近であれば能動的にリフレッシュする
      // （レスポンスストリーミング対応のLambda等では期限切れが401として返らないケースがあるため、
      //  以下の401チェックのみに頼らず、事前チェックを主とする）
      let idToken: string | null = await ensureValidIdToken();
      let res = await callAiLambda(idToken);
      // 上記の事前チェックをすり抜けた場合の保険として、401が返った場合も1回だけ再試行する
      if (res.status === 401) {
        await refreshTokenWithLock();
        idToken = getIdToken();
        if (!idToken) {
          throw new Error("Missing auth token");
        }
        res = await callAiLambda(idToken);
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      const factorsResult: FactorDef[] = Array.isArray(data.factors) ? data.factors : [];
      const overallNoteResult: string = typeof data.overallNote === "string" ? data.overallNote : "";
      onChange({
        aiRun: true,
        aiFactors: factorsResult,
        aiOverallNote: overallNoteResult,
        analysisStatus: "complete",
        aiErrorMessage: null,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      onChange({ analysisStatus: "error", aiErrorMessage: message });
      toast({ description: tr("label.aiError"), status: "error", duration: 4000 });
    }
  };

  const handleReflectConfirm = () => {
    if (state.aiFactors && state.aiFactors.length > 0) {
      onReflect?.(state.aiFactors);
    }
    onChange({ reflectConfirm: true });
    setConfirmOpen(false);
    toast({ description: tr("modal.reflectDone"), status: "success", duration: 3000 });
  };

  // ---- 入力内容＋AI分析結果をサーバーへ保存 ----
  const handleSaveAnalysis = async () => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      const salesDataLinkageInputData = JSON.stringify(buildFactorAnalysisInput());
      const salesDataLinkageAnalysisData = JSON.stringify({
        aiFactors: state.aiFactors,
        aiOverallNote: state.aiOverallNote,
      });
      const result = await WebAPI.request({
        query: `
          mutation putSalesDataLinkageAnalysis($input: SalesDataLinkageAnalysisInput) {
            putSalesDataLinkageAnalysis(input: $input) {
              status
              message
              updateDate
            }
          }
        `,
        variables: {
          input: {
            projectId,
            ...(reviewDate && { reviewDate }),
            salesDataLinkageInputData,
            salesDataLinkageAnalysisData,
            updateDate: state.updateDate || null,
          },
        },
      });
      const response = result?.data?.putSalesDataLinkageAnalysis;
      if (response?.status === "success") {
        onChange({ updateDate: response.updateDate ?? null }, true); // 保存確定 → 未保存判定の基準値を更新
        toast({ description: tr("label.aiSaveSuccess"), status: "success", duration: 3000 });
      } else if (response?.status === "expired") {
        toast({ description: tr("label.aiSaveExpired"), status: "warning", duration: 5000, isClosable: true });
      } else {
        toast({ description: tr("label.aiSaveError"), status: "error", duration: 4000 });
      }
    } catch (e) {
      console.error("Failed to save salesDataLinkage analysis", e);
      toast({ description: tr("label.aiSaveError"), status: "error", duration: 4000 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VStack align="stretch" spacing="16px">
      <Grid templateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap="20px" alignItems="stretch">
        {/* 分析インプット */}
        <GridItem
          bg="#fff"
          border="1px solid #e2e8f0"
          borderRadius="10px"
          boxShadow="0 1px 3px rgba(16,42,80,.06)"
          p="20px"
        >
          <Text fontSize="14px" fontWeight={700} color="#16233f" mb="14px">
            {tr("label.aiInputPanelTitle")}
          </Text>
          <VStack align="stretch" spacing="16px">
            {groups.map((g) => (
              <Box key={g.title}>
                <Text
                  fontSize="11px"
                  fontWeight={700}
                  color="#8a94a6"
                  letterSpacing=".04em"
                  mb="4px"
                  pb="5px"
                  borderBottom="2px solid #eef2f7"
                >
                  {g.title}
                </Text>
                {g.rows.map((row, i) => (
                  <Flex
                    key={`${row.label}-${i}`}
                    justify="space-between"
                    align="center"
                    fontSize="13px"
                    py="7px"
                    px="2px"
                    borderBottom="1px solid #f4f6fa"
                  >
                    <Text color="#64748b">{row.label}</Text>
                    <Text fontWeight={700} color={row.color}>
                      {row.value}
                    </Text>
                  </Flex>
                ))}
              </Box>
            ))}
          </VStack>
          <Button
            w="100%"
            mt="18px"
            h="auto"
            py="12px"
            bgGradient="linear(90deg, #5b3df5, #2f6bd4)"
            color="#fff"
            borderRadius="9px"
            fontSize="14px"
            fontWeight={700}
            _hover={{ opacity: 0.9 }}
            _active={{ opacity: 0.8 }}
            leftIcon={<SparkleIcon />}
            isDisabled={!isEditable || state.analysisStatus === "running"}
            isLoading={state.analysisStatus === "running"}
            loadingText={tr("label.aiRunning")}
            onClick={handleRun}
          >
            {tr("button.runAiAnalysis")}
          </Button>
          <Text fontSize="10.5px" color="#b4bdca" mt="10px" lineHeight="1.5">
            {tr("label.aiRunNote")}
          </Text>
        </GridItem>

        {/* 分析結果 */}
        <GridItem
          bg="#fff"
          border="1px solid #e2e8f0"
          borderRadius="10px"
          boxShadow="0 1px 3px rgba(16,42,80,.06)"
          p="24px"
          minH="280px"
          display="flex"
          flexDirection="column"
        >
          {state.analysisStatus === "running" ? (
            <Box m="auto" textAlign="center" color="#94a3b8">
              <Spinner size="lg" color="#7c5cff" thickness="3px" mb="12px" />
              <Text fontSize="13px" fontWeight={600} color="#64748b">
                {tr("label.aiRunning")}
              </Text>
            </Box>
          ) : state.analysisStatus === "complete" && state.aiFactors ? (
            <>
              <HStack spacing="8px" mb="4px">
                <Text fontSize="14px" color="#16233f" fontWeight={700}>
                  {tr("label.aiResultTitle")}
                </Text>
                <Text
                  fontSize="11px"
                  fontWeight={700}
                  color="#7c5cff"
                  bg="#f0edff"
                  borderRadius="5px"
                  px="8px"
                  py="2px"
                >
                  {tr("label.aiResultBadge")}
                </Text>
              </HStack>
              <Text fontSize="11.5px" color="#94a3b8" mb="14px">
                {tr("label.aiResultNote")}
              </Text>
              {state.aiOverallNote && (
                <Box bg="#f8f7ff" border="1px solid #e4defc" borderRadius="8px" px="12px" py="10px" mb="14px">
                  <Text fontSize="10.5px" fontWeight={700} color="#7c5cff" mb="3px">
                    {tr("label.aiOverallNoteLabel")}
                  </Text>
                  <Text fontSize="12.5px" color="#334155" lineHeight="1.6">
                    {state.aiOverallNote}
                  </Text>
                </Box>
              )}
              <VStack align="stretch" spacing="10px">
                {factors.map((f) => (
                  <Box key={f.label}>
                    <Flex justify="space-between" fontSize="12px" color="#475569" mb="3px">
                      <Text>{f.label}</Text>
                      <Text fontWeight={700} color={f.color}>
                        {f.pct}
                      </Text>
                    </Flex>
                    <Box h="6px" bg="#eef2f7" borderRadius="4px" overflow="hidden">
                      <Box h="100%" w={`${f.width}%`} bg={f.color} borderRadius="4px" />
                    </Box>
                  </Box>
                ))}
              </VStack>
              <HStack spacing="10px" mt="20px" align="stretch">
                <Button
                  flex="1"
                  h="auto"
                  py="12px"
                  variant="outline"
                  borderColor="#2f6bd4"
                  color="#2f6bd4"
                  borderRadius="9px"
                  fontSize="14px"
                  fontWeight={700}
                  isDisabled={!isEditable || !projectId}
                  isLoading={isSaving}
                  loadingText={tr("button.saveAiResult")}
                  onClick={handleSaveAnalysis}
                >
                  {tr("button.saveAiResult")}
                </Button>
                <Button
                  flex="1"
                  h="auto"
                  py="12px"
                  bg="#2f6bd4"
                  color="#fff"
                  borderRadius="9px"
                  fontSize="14px"
                  fontWeight={700}
                  _hover={{ bg: "#1e4fa0" }}
                  leftIcon={<CheckIcon />}
                  isDisabled={!isEditable || state.reflectConfirm}
                  onClick={() => setConfirmOpen(true)}
                >
                  {state.reflectConfirm ? tr("modal.reflectDone") : tr("button.reflectToSimulation")}
                </Button>
              </HStack>
              {state.updateDate && (
                <Text fontSize="10.5px" color="#94a3b8" mt="8px" textAlign="right">
                  {tr("label.aiSavedAt", { date: state.updateDate })}
                </Text>
              )}
            </>
          ) : (
            <Box m="auto" textAlign="center" color="#94a3b8">
              {state.analysisStatus === "error" ? (
                <>
                  <Text fontSize="13px" fontWeight={600} color="#dc2626">
                    {tr("label.aiError")}
                  </Text>
                  {state.aiErrorMessage && (
                    <Text fontSize="11.5px" mt="5px">
                      {state.aiErrorMessage}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Box display="inline-block" mb="10px" color="#cbd5e1">
                    <SparkleIcon size={40} strokeWidth={1.6} />
                  </Box>
                  <Text fontSize="13px" fontWeight={600} color="#64748b">
                    {tr("label.aiNoResultTitle")}
                  </Text>
                  <Text fontSize="11.5px" mt="5px">
                    {tr("label.aiNoResultDesc")}
                  </Text>
                </>
              )}
            </Box>
          )}
        </GridItem>
      </Grid>

      <Modal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{tr("modal.reflectConfirmTitle")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text whiteSpace="pre-line">{tr("modal.reflectConfirmMessage")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setConfirmOpen(false)}>
              {tr("modal.no")}
            </Button>
            <Button colorScheme="blue" onClick={handleReflectConfirm}>
              {tr("modal.yes")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AiStep;
